import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Body, Button, ButtonGroup, H2 } from "@datapay-by-datacom/design-system";
import { ImportProgress } from "../../shared";
import { SplitCostingWizardCtx } from "../components/SplitCostingWizardContext";
import { SplitCostingValidationReport } from "../components/SplitCostingValidationReport";
import styles from "./ValidateScreen.module.css";

const UPLOAD_ROUTE = "/onboarding/split-costing-import";
const RESULT_ROUTE = "/onboarding/split-costing-import/result";

/**
 * Step 2 of the split costing import wizard — Validate.
 *
 * Rendered as a child route of `SplitCostingWizardProvider`, so `parsedRows`
 * is only non-null once the upload step (step 1) succeeded — a direct visit
 * to this route with no prior upload renders a "nothing to validate" guard
 * instead of throwing.
 *
 * Mounting this screen with rows present auto-triggers the
 * `split_costing_import` validate workflow (mirrors the work pattern
 * import's "auto validate pre-flight" — see
 * `src/work-patterns/hooks/WorkPatternImport/useWorkPatternImportInvocation.ts`)
 * — there is no explicit "Validate" button.
 *
 * Continue to commit is enabled only once the validate workflow has
 * completed with zero blocking errors (`validationReport.failed === 0`) and
 * every portal warning (AC-APP-04/AC-APP-07 only — rendered by
 * `SplitCostingValidationReport`) has been acknowledged (R-GOV-2, R-VAL-1).
 * Datapay-aware blocking rules from the API (R-APP-4, R-VAL-4 / AC-APP-09)
 * arrive only in `report.errors`, not as warn-and-acknowledge warnings.
 * "Continue to commit" calls the manager's
 * `commit()` with the current `warningsAcknowledged` state and navigates to
 * the result screen immediately — the hook itself re-derives the guard
 * (stale validation, remaining errors, unacknowledged warnings) rather than
 * trusting the button's disabled state alone. The manager is read from
 * `wizardCtx.manager` (a single instance lifted into
 * `SplitCostingWizardProvider`, not instantiated per-screen) so the commit
 * poll survives the route change to the result screen — see that provider's
 * doc comment.
 *
 * Cancel discards the in-progress validation (clearing both the manager's
 * invocation state and the wizard's upload state) and returns to the upload
 * step — nothing was persisted server-side by validate, so there is nothing
 * to roll back beyond client state (AC-NFR-01).
 */
export function ValidateScreen() {
  const wizardCtx = useContext(SplitCostingWizardCtx);
  const navigate = useNavigate();
  const hasTriggeredRef = useRef(false);
  const [warningsAcknowledged, setWarningsAcknowledged] = useState(false);

  if (!wizardCtx) {
    throw new Error("ValidateScreen must be rendered inside SplitCostingWizardProvider");
  }

  const {
    companyCode,
    companyName,
    mypayEnvironment,
    fileName,
    parsedRows,
    clearUpload,
    manager,
  } = wizardCtx;

  useEffect(() => {
    if (!parsedRows || parsedRows.length === 0) return;
    // The onboarding agent ids load asynchronously. Triggering before they
    // resolve trips `validate()`'s "not available in this environment" guard
    // against a config that is merely still loading, and the ref guard below
    // means nothing would retry once it arrives.
    if (manager.isConfigLoading) return;
    if (hasTriggeredRef.current) return;
    hasTriggeredRef.current = true;

    manager.setPendingRows(parsedRows, companyCode, mypayEnvironment, fileName);
    void manager.validate();
    // `manager` intentionally excluded — its functions are re-created every
    // render, and the ref guard above already ensures this only fires once
    // per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedRows, companyCode, mypayEnvironment, fileName, manager.isConfigLoading]);

  const handleWarningsAcknowledgedChange = useCallback(
    (allAcknowledged: boolean) => {
      setWarningsAcknowledged(allAcknowledged);
    },
    []
  );

  // R-GOV-2 / R-VAL-1: zero blocking errors *and* every portal warning
  // acknowledged. `warningsAcknowledged` defaults to `false` and is only set
  // by `SplitCostingValidationReport`'s callback once it has computed the
  // warning set for the current report (including the trivial "zero
  // warnings" case), so there is no report for which this reads `true`
  // without the child having actually evaluated it.
  const canContinueToCommit =
    !!manager.validationReport &&
    manager.validationReport.failed === 0 &&
    warningsAcknowledged &&
    !manager.isCommitting;

  // Navigates to the result screen immediately rather than awaiting the
  // commit poll here — a commit over hundreds of rows should not look
  // frozen on this screen. The manager instance (and its in-flight commit
  // poll) is lifted into `SplitCostingWizardCtx`, so it survives the route
  // change and the result screen reads the same poll rather than starting a
  // fresh one.
  const handleContinueToCommit = useCallback(() => {
    void manager.commit(warningsAcknowledged);
    navigate(RESULT_ROUTE);
    // `manager` intentionally excluded — see the trigger effect above for why.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warningsAcknowledged, navigate]);

  // AC-NFR-01: validate never persists anything server-side, so "cancel" only
  // needs to discard client state — the manager's invocation/report state and
  // the wizard's upload state — then return to the upload step.
  const handleCancel = useCallback(() => {
    manager.clear();
    clearUpload();
    navigate(UPLOAD_ROUTE);
    // `manager` intentionally excluded — see the trigger effect above for why.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearUpload, navigate]);

  if (!parsedRows || parsedRows.length === 0) {
    return (
      <div className={styles.container}>
        <H2>Validate import</H2>
        <Body>No import to validate. Please upload a CSV first.</Body>
        <Button onPress={() => navigate(UPLOAD_ROUTE)}>Back to upload</Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <H2>Validate split costing import</H2>
        <Body>
          Validating for <strong>{companyName ?? companyCode}</strong>. Rows
          are checked against MyPay before anything is written — nothing is
          committed yet.
        </Body>
      </div>

      {manager.isValidating && (
        <ImportProgress
          heading="Validating import…"
          progressbarLabel="Validation in progress"
          detail={`Checking ${parsedRows.length} row${
            parsedRows.length === 1 ? "" : "s"
          }…`}
        />
      )}

      {manager.managerError && (
        <div className={styles.errorBanner} role="alert">
          <strong>Validation failed.</strong> {manager.managerError}
        </div>
      )}

      {manager.validationReport && (
        <>
          <SplitCostingValidationReport
            report={manager.validationReport}
            rows={parsedRows}
            onWarningsAcknowledgedChange={handleWarningsAcknowledgedChange}
          />
          <ButtonGroup align="right">
            <Button variant="secondary" onPress={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={!canContinueToCommit}
              onPress={handleContinueToCommit}
            >
              Continue to commit
            </Button>
          </ButtonGroup>
        </>
      )}
    </div>
  );
}
